import classnames from "classnames";
import { orderBy, where } from "firebase/firestore";
import Moment from "moment";
import React, { useMemo } from "react";
import {
    Accordion,
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    Col,
    Dropdown,
    DropdownButton,
    Figure,
    ListGroup,
    Modal,
    Row,
} from "react-bootstrap";
import DropdownItem from "react-bootstrap/esm/DropdownItem.js";
import { BiSolidError } from "react-icons/bi";
import {
    FaAngleDown,
    FaAngleUp,
    FaCalendar,
    FaCheck,
    FaClipboard,
    FaCode,
    FaExclamationTriangle,
    FaImage,
    FaLink,
    FaMap,
    FaPlay,
    FaPlus,
    FaTimes,
    FaTrash,
    FaTrashRestore,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import {
    MissionCollectionGroup,
    MissionDocument,
    useFlightCollection,
    useLocationDocument,
    useMissionDocument,
    useResultCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import * as missionApi from "../../api/missionApi";
import { useDroneMissionsRtdbDocuments } from "../../api/rtdbWrappers.generated.jsx";
import AddAppModal from "../../components/AddAppModal/index.jsx";
import AddExportModal from "../../components/AddExportModal/index.jsx";
import AppInfoBadge from "../../components/AppInfo";
import AppStatusMessage from "../../components/AppStatusMessage/index.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import CollectStatusMessage from "../../components/CollectStatusMessage";
import {
    CorvusList,
    CorvusListItem,
    CorvusListItemText,
} from "../../components/CorvusList";
import DevMenuButton from "../../components/DevMenuButton";
import { DownloadButton } from "../../components/DownloadButton/index.jsx";
import DroneCardList from "../../components/DroneCardList/index.jsx";
// import AbortButton from "../../components/AbortButton";
import DroneCollectStatusMessage from "../../components/DroneCollectStatusMessage";
import ExportInfoBadge from "../../components/ExportInfo";
import ExportPartStatusMessage from "../../components/ExportPartStatusMessage";
import ExportStatusMessage from "../../components/ExportStatusMessage";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import FlightInfoBadge from "../../components/FlightInfo";
import Layout, { Title } from "../../components/Layout/Layout";
import MapButton from "../../components/MapButton";
import MeasurementInfoBadge from "../../components/MeasurementInfo";
import ProcessingStatusMessage from "../../components/ProcessingStatusMessage/index.jsx";
import ResultCardList from "../../components/ResultCardList/index.jsx";
import StorageDevMenuItem from "../../components/StorageDevMenuItem";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import UploadStatusMessage from "../../components/UploadStatusMessage";
import UserQuestion from "../../components/UserQuestion";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase.js";
import useIsSmallScreen from "../../hooks/useIsSmallScreen.jsx";
import urls from "../../urls.jsx";
import { CommandPreview } from "../TaskPage/index.jsx";

function findApp(apps, app) {
    return apps.find((candidate) => candidate.id === app.id);
}

function MissionCollectList(props) {
    const { organizationId, locationId } = useParams();
    const [flights] = useFlightCollection(organizationId, locationId, [
        where("mission.id", "==", props.mission.id),
        orderBy("timestamp", "asc"),
    ]);

    return (
        <CorvusList variant="flush" className="p-0">
            {flights.map((flight) => {
                return (
                    <CorvusListItem
                        className="bg-light p-0 py-2"
                        as={Link}
                        to={urls.flight(organizationId, locationId, flight.id)}
                        key={`collecting-${flight.id}`}
                    >
                        <CorvusListItemText className="p-0">
                            <DroneCollectStatusMessage
                                mission={props.mission}
                                flight={flight}
                                organizationId={organizationId}
                                locationId={locationId}
                            />
                        </CorvusListItemText>
                    </CorvusListItem>
                );
            })}
        </CorvusList>
    );
}

function MissionProcessList(props) {
    const [apps] = api.useCorvusApps();
    const [missionHandlerStarted, setMissionHandlerStarted] =
        React.useState(false);

    React.useEffect(() => {
        if (missionHandlerStarted && props.mission.running === false) {
            console.log("Set mission handler stopped");
            setMissionHandlerStarted(false);
        }
    }, [missionHandlerStarted, props.mission.running]);

    return (
        <CorvusList variant="flush" className="p-0">
            {missionApi
                .getMissionApps(props.mission)
                .filter((app) => app?.type !== "background")
                .map((app) => {
                    return (
                        <ListGroup.Item
                            className="bg-light p-0 py-2"
                            key={`processing-${app.id}`}
                        >
                            <CorvusListItemText className="p-0">
                                <AppStatusMessage
                                    mission={props.mission}
                                    app={findApp(apps, app)}
                                    allApps={apps}
                                    missionHandlerStarted={
                                        missionHandlerStarted
                                    }
                                    setMissionHandlerStarted={
                                        setMissionHandlerStarted
                                    }
                                />
                            </CorvusListItemText>
                        </ListGroup.Item>
                    );
                })}
        </CorvusList>
    );
}

function MissionUploadList({ mission, className, onClick }) {
    const { organizationId, locationId, missionId } = useParams();
    const [measurementUrls, setMeasurementUrls] = React.useState([]);
    const shownMeasurements = useMemo(() => {
        return (
            mission?.measurements?.slice(
                Math.max((mission.measurements?.length ?? 0) - 4, 0),
            ) ?? []
        );
    }, [mission?.measurements]);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const urls = await Promise.all(
                    shownMeasurements.map((measurement) => {
                        return getFirebaseStorageDownloadUrl(
                            `organizations/${organizationId}/locations/${locationId}/measurements/512x512/${measurement.id}_512x512.jpg`,
                        );
                    }),
                );

                setMeasurementUrls(urls);
            } catch (error) {
                console.error("Error fetching data:", error);
                // Handle error if needed
            }
        };

        if (!mission?.measurementsDeletedAt) {
            fetchData();
        }
    }, [
        shownMeasurements,
        locationId,
        organizationId,
        mission?.measurementsDeletedAt,
    ]);

    return (
        <CorvusList variant="flush" className="p-0">
            {mission?.measurementsDeletedAt ? (
                <ListGroup.Item
                    variant="danger"
                    className="px-3 py-2 d-flex align-items-center"
                >
                    <FaTrash className="ms-1 me-4 flex-shrink-0" />
                    <div>
                        Measurements deleted at{" "}
                        <TimeDisplay
                            timestamp={mission?.measurementsDeletedAt.seconds}
                            format="YYYY-MM-DD HH:mm:ss"
                            organizationId={organizationId}
                            locationId={locationId}
                        />
                    </div>
                </ListGroup.Item>
            ) : (
                <ListGroup.Item
                    as={Link}
                    to={urls.missionMeasurements(
                        organizationId,
                        locationId,
                        missionId,
                    )}
                    action
                    onClick={onClick}
                    className={classnames("bg-light px-4 py-2", className)}
                >
                    <Row
                        className={classnames(
                            "d-flex justify-content-between align-items-start",
                            className,
                        )}
                    >
                        {measurementUrls.length > 0
                            ? measurementUrls.map((measurementUrl) => (
                                  <Col xs={3} key={measurementUrl}>
                                      <Figure.Image
                                          src={measurementUrl}
                                          rounded
                                      />
                                  </Col>
                              ))
                            : "No measurements"}
                    </Row>
                </ListGroup.Item>
            )}
        </CorvusList>
    );
}

function MissionExportList({ mission }) {
    const [missionHandlerStartRequested, setMissionHandlerStartRequested] =
        React.useState(false);

    React.useEffect(() => {
        if (mission.running) {
            setMissionHandlerStartRequested(false);
        }
    }, [mission.running]);

    const exportStates = mission?.cloudExportStates ?? {};

    return (
        <CorvusList variant="flush" className="p-0">
            {Object.keys(exportStates)
                .filter(
                    (exportPart) =>
                        exportStates[exportPart] !== missionApi.STATE_OMITTED,
                )
                .sort()
                .map((exportPart) => (
                    <ListGroup.Item
                        className="bg-light p-0 py-2"
                        key={exportPart}
                    >
                        <CorvusListItemText className="p-0">
                            <ExportPartStatusMessage
                                mission={mission}
                                exportPart={exportPart}
                                exportState={exportStates[exportPart]}
                                missionHandlerStartRequested={
                                    missionHandlerStartRequested
                                }
                                setMissionHandlerStartRequested={
                                    setMissionHandlerStartRequested
                                }
                            />
                        </CorvusListItemText>
                    </ListGroup.Item>
                ))}
        </CorvusList>
    );
}

function MissionCollectAccordionItem(props) {
    return (
        <Accordion.Item eventKey={props.eventKey}>
            <Accordion.Header>
                <Col className="d-flex justify-content-between align-items-center">
                    <CollectStatusMessage mission={props.mission} />
                    <FlightInfoBadge mission={props.mission} />
                </Col>
            </Accordion.Header>
            <Accordion.Body className="p-0">
                <MissionCollectList mission={props.mission} />
            </Accordion.Body>
        </Accordion.Item>
    );
}

function MissionUploadAccordionItem(props) {
    return (
        <Accordion.Item eventKey={props.eventKey}>
            <Accordion.Header>
                <Col className="d-flex justify-content-between align-items-start">
                    <UploadStatusMessage mission={props.mission} />
                    <MeasurementInfoBadge
                        mission={props.mission}
                        className="me-1"
                    />
                </Col>
            </Accordion.Header>
            <Accordion.Body className="p-0">
                <MissionUploadList mission={props.mission} />
            </Accordion.Body>
        </Accordion.Item>
    );
}

function MissionProcessAccordionItem(props) {
    return (
        <Accordion.Item eventKey={props.eventKey}>
            <Accordion.Header>
                <Col className="d-flex justify-content-between align-items-center">
                    <ProcessingStatusMessage mission={props.mission} />
                    <AppInfoBadge mission={props.mission}></AppInfoBadge>
                </Col>
            </Accordion.Header>
            <Accordion.Body className="p-0">
                <MissionProcessList mission={props.mission} />
            </Accordion.Body>
        </Accordion.Item>
    );
}

function MissionExportAccordionItem(props) {
    const connectionsOmmitted =
        props?.mission?.states?.exporting || missionApi.STATE_OMITTED;
    const show = connectionsOmmitted !== missionApi.STATE_OMITTED;

    return (
        show && (
            <Accordion.Item eventKey={props.eventKey}>
                <Accordion.Header>
                    <Col className="d-flex justify-content-between align-items-center">
                        <ExportStatusMessage mission={props.mission} />
                        <ExportInfoBadge
                            mission={props.mission}
                        ></ExportInfoBadge>
                    </Col>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                    <MissionExportList mission={props.mission} />
                </Accordion.Body>
            </Accordion.Item>
        )
    );
}

function MissionAccordion(props) {
    return (
        <Accordion className="mb-4">
            <MissionCollectAccordionItem
                mission={props.mission}
                eventKey="collecting"
            />
            <MissionUploadAccordionItem
                mission={props.mission}
                eventKey="uploading"
            />
            <MissionProcessAccordionItem
                mission={props.mission}
                eventKey="processing"
            />
            <MissionExportAccordionItem
                mission={props.mission}
                eventKey="exporting"
            />
        </Accordion>
    );
}

function MissionLog(props) {
    return (
        <>
            {props.events
                .slice()
                .reverse()
                .map((event, index) => (
                    <ListGroup.Item
                        // biome-ignore lint/suspicious/noArrayIndexKey: Mission events have no guaranteed unique identifier and may share timestamps.
                        key={`event-${index}`}
                        className="px-4 py-2"
                    >
                        <div>
                            <b>
                                {props.events.length - index}. {event.type}
                            </b>
                        </div>
                        <div className="ms-4">
                            <code>timestamp{": "}</code>
                            <code>
                                {Moment.unix(event.timestamp.seconds).format(
                                    "YYYY-MM-DD HH:mm:ss:SSS",
                                )}
                            </code>
                        </div>
                        {Object.keys(event)
                            .filter(
                                (key) => !["type", "timestamp"].includes(key),
                            )
                            .sort()
                            .map((key) => (
                                <div key={key} className="ms-4">
                                    <code>
                                        {key}
                                        {": "}
                                    </code>
                                    <code>{JSON.stringify(event[key])}</code>
                                </div>
                            ))}
                    </ListGroup.Item>
                ))}
        </>
    );
}

export function MissionHandlerAlerts({
    organizationId,
    locationId,
    missionId,
    heading,
}) {
    const [showAllAlerts, setShowAllAlerts] = React.useState(false);
    const [location] = useLocationDocument(organizationId, locationId);
    const [mission] = useMissionDocument(organizationId, locationId, missionId);
    const alerts = useMemo(
        () => mission?.mission_handler?.alerts || [],
        [mission?.mission_handler?.alerts],
    );

    const { getUserIsAdmin } = useUser();
    const isSmallScreen = useIsSmallScreen();

    const levelToVariantMap = {
        info: "primary",
        warning: "warning",
        warn: "warning",
        error: "danger",
    };

    function toggleShowAllAlerts() {
        setShowAllAlerts(!showAllAlerts);
    }

    function hasAlerts() {
        return alerts.length > 0;
    }

    return (
        hasAlerts() && (
            <Card.Body className="p-4 pt-0">
                {showAllAlerts ? (
                    alerts
                        .slice()
                        .reverse()
                        .map((alert, index) => (
                            <Alert
                                // biome-ignore lint/suspicious/noArrayIndexKey: Mission alerts have no guaranteed unique identifier and may share timestamps or content.
                                key={index}
                                variant={
                                    levelToVariantMap[alert.level] || "primary"
                                }
                                className={`mb-1 ${index !== 0 ? "pb-1 pt-1" : ""}`}
                            >
                                {heading && index === 0 && (
                                    <Alert.Heading
                                        style={{
                                            fontSize: "1rem",
                                            lineHeight: "0.5",
                                        }}
                                    >
                                        {heading}
                                    </Alert.Heading>
                                )}
                                <div className="d-flex justify-content-between align-items-center">
                                    <span
                                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Alert messages may contain intentional HTML; preserve existing rendering behavior.
                                        dangerouslySetInnerHTML={{
                                            __html: alert.message,
                                        }}
                                    />
                                    <div className="d-flex align-items-center">
                                        <Badge bg="secondary" className="ms-2">
                                            <TimeDisplay
                                                timestamp={alert.date.seconds}
                                                format="YYYY-MM-DD#HH:mm:ss"
                                                hideDateIfToday={true}
                                                breakOnHashtag={isSmallScreen}
                                                location={location}
                                            />
                                        </Badge>
                                        {getUserIsAdmin() && index === 0 && (
                                            <Button
                                                variant="link"
                                                onClick={toggleShowAllAlerts}
                                                className="p-0"
                                            >
                                                <FaAngleUp
                                                    color="grey"
                                                    className="ms-3"
                                                />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Alert>
                        ))
                ) : (
                    <Alert
                        variant={
                            levelToVariantMap[
                                alerts[alerts.length - 1].level
                            ] || "primary"
                        }
                        className="m-0"
                    >
                        {heading && (
                            <Alert.Heading
                                style={{ fontSize: "1rem", lineHeight: "0.5" }}
                            >
                                {heading}
                            </Alert.Heading>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                            <span
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: Alert messages may contain intentional HTML; preserve existing rendering behavior.
                                dangerouslySetInnerHTML={{
                                    __html: alerts[alerts.length - 1].message,
                                }}
                            />
                            <div className="d-flex align-items-center">
                                <Badge bg="secondary" className="ms-2">
                                    <TimeDisplay
                                        timestamp={
                                            alerts[alerts.length - 1].date
                                                .seconds
                                        }
                                        format="YYYY-MM-DD#HH:mm:ss"
                                        hideDateIfToday={true}
                                        breakOnHashtag={isSmallScreen}
                                        location={location}
                                    />
                                </Badge>
                                {getUserIsAdmin() && (
                                    <Button
                                        variant="link"
                                        onClick={toggleShowAllAlerts}
                                        className="p-0"
                                    >
                                        <FaAngleDown
                                            color="grey"
                                            className="ms-3"
                                        />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Alert>
                )}
            </Card.Body>
        )
    );
}

export default function MissionPage() {
    const { organizationId, locationId, missionId } = useParams();
    const [view, setView] = React.useState("default");
    const [showAddAppModal, setShowAddAppModal] = React.useState(false);
    const [showAddExportModal, setShowAddExportModal] = React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showEvents, setShowEvents] = React.useState(false);
    const [showResumeModal, setShowResumeModal] = React.useState(false);
    const [showAbortModal, setShowAbortModal] = React.useState(false);
    const [showDeleteMeasurementsModal, setShowDeleteMeasurementsModal] =
        React.useState(false);
    const [mission] = useMissionDocument(organizationId, locationId, missionId);
    const [logUrl, setLogUrl] = React.useState(undefined);
    const [droneConfigs, isLoadingDroneConfigs, droneConfigsError] =
        api.useDroneConfigsFromOrganization(organizationId, { type: "map" });
    const accessibleMissionDroneIds = useMemo(() => {
        if (isLoadingDroneConfigs || droneConfigsError) {
            return [];
        }

        return (mission?.droneIds ?? []).filter(
            (droneId) => droneConfigs[droneId],
        );
    }, [
        mission?.droneIds,
        droneConfigs,
        isLoadingDroneConfigs,
        droneConfigsError,
    ]);
    const [droneMissions] = useDroneMissionsRtdbDocuments(
        accessibleMissionDroneIds,
        { type: "map" },
    );
    const [resultDocs] = useResultCollection(
        organizationId,
        locationId,
        missionId,
    );

    const { getUserIsAdmin, userClaims } = useUser();
    const taskCommands = mission?.task?.commands ?? [];

    const navigate = useNavigate();

    function resumeMission() {
        setShowResumeModal(false);
        api.storeResumeEvent(organizationId, locationId, missionId);
        return api.restartMissionHandler(organizationId, locationId, missionId);
    }

    function abortMission() {
        setShowAbortModal(false);
        api.storeAbortMissionEvent(organizationId, locationId, missionId);
        api.cleanUpMissionHandler(organizationId, locationId, missionId);
    }

    function deleteMission() {
        // if the mission is still running, abort it
        if (mission?.running) {
            mission?.droneIds.forEach((droneId) => {
                if (
                    droneMissions?.[droneId]?.some(
                        (m) => m.missionId === missionId,
                    )
                ) {
                    // If the drone of that mission is still active for this mission
                    // send an abort event to that drone. We don't need to fill the
                    // mission list because those are handled below
                    api.sendAbort(droneId, []);
                }
            });
            api.storeAbortMissionEvent(organizationId, locationId, missionId);
            api.cleanUpMissionHandler(organizationId, locationId, missionId);
        }
        return api.deleteMission(organizationId, locationId, missionId);
    }

    React.useEffect(() => {
        if (!organizationId && !locationId && missionId) {
            MissionCollectionGroup.list([
                where("id", "==", missionId),
                ...(getUserIsAdmin()
                    ? []
                    : // This part of the query is needed so firebase security rules can ensure the document can be provided to the user
                      [
                          where(
                              "organizationId",
                              "==",
                              userClaims?.organizationId,
                          ),
                      ]),
            ]).then((missions) => {
                if (missions.length > 0) {
                    navigate(
                        urls.locationMission(
                            missions[0].organizationId,
                            missions[0].locationId,
                            missionId,
                        ),
                        { replace: true },
                    );
                }
            });
        }
    }, [
        organizationId,
        locationId,
        missionId,
        navigate,
        getUserIsAdmin,
        userClaims,
    ]);

    function toggleShowEvents() {
        setShowEvents(!showEvents);
    }

    function getMissionDrones(mission) {
        // TODO : This would be cleaner since we don't need the drone array. That array is solely used for this purpose. Not all mission.flights have drone id set currently.
        // const allDrones = mission.flights.map((flight) => flight.drone.id);
        const allDrones = missionApi
            .getMissionDrones(mission)
            .map((drone) => drone.id);
        return [...new Set(allDrones)];
    }

    React.useEffect(() => {
        if (mission?.clusterJob && mission?.timestamp) {
            try {
                const offsetMs = 30_000; // Add 30 seconds before the mission timestamp to ensure all log lines are included
                const milliseconds =
                    mission.timestamp.seconds * 1000 +
                    mission.timestamp.nanoseconds / 1_000_000 -
                    offsetMs;
                const missionTime = new Date(milliseconds);
                const clusterJobs = Array.isArray(mission.clusterJob)
                    ? mission.clusterJob
                    : [mission.clusterJob];
                const orConditions = clusterJobs
                    .filter((job) => job.includes("handler")) // Only include jobs with "handler" these are k8s jobs
                    .map(
                        (job) =>
                            `resource.labels.container_name="${job}-container"`,
                    ) // Map remaining jobs
                    .join(" OR "); // Join with " OR "
                if (orConditions) {
                    const containerCondition =
                        clusterJobs.length > 1
                            ? `(${orConditions})`
                            : orConditions;
                    const queryLines = [
                        'resource.type="k8s_container"',
                        containerCondition,
                        'resource.labels.namespace_name="default"',
                        `timestamp >= "${missionTime.toISOString()}"`,
                    ];
                    const query = queryLines.join("\n");
                    const encodedQuery = encodeURIComponent(query);

                    setLogUrl(
                        `https://console.cloud.google.com/logs/query?query=${encodedQuery}`,
                    );
                } else {
                    setLogUrl(undefined);
                }
            } catch (error) {
                console.log(`Error in setting log URL: ${error}`);
            }
        }
    }, [mission]);

    if (!mission) {
        return null;
    }

    function changeAppError() {
        if (mission?.appError) {
            api.removeAppError(
                mission.organizationId,
                mission.locationId,
                mission.id,
            );
        } else {
            api.addAppError(
                mission.organizationId,
                mission.locationId,
                mission.id,
            );
        }
    }

    return (
        <Layout>
            <title>{`Mission ${mission?.name} | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.mission}
                mission={mission ?? null}
            />

            <Modal
                show={showResumeModal}
                onHide={() => setShowResumeModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Resume Mission</Modal.Title>
                </Modal.Header>
                <Modal.Body>Resume mission now?</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowResumeModal(false)}
                    >
                        Cancel
                    </Button>
                    <Dropdown as={ButtonGroup} align={{ lg: "start" }}>
                        <Button variant="primary" onClick={resumeMission}>
                            <FaPlay
                                className="me-2"
                                style={{ marginTop: "-2px" }}
                            />
                            Resume
                        </Button>
                        {getMissionDrones(mission).length === 1 && ( // Preview is currently only well defined when there is only one drone in the mission
                            <>
                                <Dropdown.Toggle split variant="primary" />
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        as={Link}
                                        target="_blank"
                                        to={`${urls.locationMap(organizationId, locationId)}?missionId=${missionId}&droneId=${getMissionDrones(mission)[0]}`}
                                    >
                                        <FaMap
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Preview
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </>
                        )}
                    </Dropdown>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showAbortModal}
                onHide={() => setShowAbortModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Abort Mission</Modal.Title>
                </Modal.Header>
                <Modal.Body>Abort mission now?</Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowAbortModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={abortMission}>
                        <FaTimes
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Abort
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showDeleteMeasurementsModal}
                onHide={() => setShowDeleteMeasurementsModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete measurements of mission</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the measurements of this
                    mission? This is not reversible!
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDeleteMeasurementsModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            await api.requestDeleteMissionMeasurements(
                                organizationId,
                                locationId,
                                missionId,
                            );
                            setShowDeleteMeasurementsModal(false);
                        }}
                    >
                        <FaTrash
                            variant="danger"
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            <AddAppModal
                organizationId={organizationId}
                locationId={locationId}
                mission={mission}
                show={showAddAppModal}
                hide={() => setShowAddAppModal(false)}
            />

            <AddExportModal
                organizationId={organizationId}
                locationId={locationId}
                mission={mission}
                show={showAddExportModal}
                hide={() => setShowAddExportModal(false)}
            />

            <Card className="mb-4">
                <Title
                    backButtonUrl={urls.locationMissions(
                        organizationId,
                        locationId,
                    )}
                    title={
                        <>
                            {mission?.task?.name}
                            {missionApi.getMissionState(mission) ===
                                missionApi.STATE_MISSED && (
                                <Badge
                                    pill
                                    bg={null}
                                    className="ms-2 align-middle"
                                    style={{
                                        backgroundColor: "#f07e27",
                                        fontSize: "12px",
                                    }}
                                >
                                    missed
                                </Badge>
                            )}
                            <div
                                className="text-muted"
                                style={{ fontSize: "16px" }}
                            >
                                <TimeDisplay
                                    timestamp={mission.timestamp.seconds}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    organizationId={organizationId}
                                    locationId={locationId}
                                />
                            </div>
                        </>
                    }
                >
                    {/* <AbortButton
                        mission={mission}
                        className="ms-2 mb-1"
                    /> */}
                    <MapButton
                        url={`${urls.locationMap(organizationId, locationId)}?missionId=${missionId}`}
                        className="ms-2 mb-1"
                    />
                    <ButtonGroup className="ms-2 mb-1">
                        {getUserIsAdmin() && (
                            <DevMenuButton>
                                <Dropdown.Item
                                    as={Link}
                                    to={`${urls.locationMap(organizationId, locationId)}?missionId=${missionId}`}
                                >
                                    <FaMap
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    View Map
                                </Dropdown.Item>
                                <Dropdown.Item onClick={toggleShowEvents}>
                                    <FaCalendar
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    {showEvents ? "Hide Events" : "View Events"}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() =>
                                        setView(
                                            view === "default"
                                                ? "data"
                                                : "default",
                                        )
                                    }
                                >
                                    <FaCode
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    {view === "default" && <>View Data</>}
                                    {view === "data" && <>View Default</>}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() =>
                                        missionApi.setMissionSucceeded(
                                            organizationId,
                                            locationId,
                                            missionId,
                                        )
                                    }
                                >
                                    <FaCheck
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    <>Set Succeeded</>
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => changeAppError()}>
                                    <BiSolidError
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    {mission?.appError
                                        ? "Remove app error"
                                        : "Set app error"}
                                </Dropdown.Item>
                                {/* <Dropdown.Item
                                    onClick={() => setShowDeleteMeasurementsModal(true)}>
                                    <FaTrash
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    Delete measurements
                                </Dropdown.Item> */}
                                <Dropdown.Divider />
                                <FirestoreDevMenuItem
                                    as={Dropdown.Item}
                                    path={
                                        MissionDocument(
                                            organizationId,
                                            locationId,
                                            missionId,
                                        ).path
                                    }
                                />
                                <StorageDevMenuItem
                                    as={Dropdown.Item}
                                    path={
                                        MissionDocument(
                                            organizationId,
                                            locationId,
                                            missionId,
                                        ).path
                                    }
                                />
                                {logUrl && (
                                    <DropdownItem
                                        onClick={() => {
                                            window.open(logUrl);
                                        }}
                                    >
                                        <FaLink
                                            className="me-2 text-primary"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Logs (k8s)
                                    </DropdownItem>
                                )}
                            </DevMenuButton>
                        )}
                        <DropdownButton
                            variant="outline-secondary"
                            align="end"
                            as={ButtonGroup}
                            title="Mission"
                        >
                            <Dropdown.Item
                                onClick={() =>
                                    navigate(
                                        urls.task(
                                            organizationId,
                                            locationId,
                                            mission.task.id,
                                        ),
                                    )
                                }
                            >
                                <FaClipboard
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Task
                            </Dropdown.Item>
                            {(mission?.measurements ?? []).length > 0 &&
                                !mission?.measurementsDeletedAt && (
                                    <DownloadButton
                                        as={Dropdown.Item}
                                        downloadItems={[
                                            api.toMeasurementQueryDownloadItem({
                                                organizationId,
                                                locationId,
                                                missionId,
                                            }),
                                        ]}
                                        checkAvailability={false}
                                        filename={`measurements_${missionId}.zip`}
                                        buttonText={"Download images"}
                                        variant="outline-secondary"
                                        icon={
                                            <span className="me-2">
                                                <FaImage />
                                            </span>
                                        }
                                    />
                                )}
                            {getUserIsAdmin() && (
                                <>
                                    <Dropdown.Item
                                        onClick={() => setShowAddAppModal(true)}
                                    >
                                        <FaPlus
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Add app
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() =>
                                            setShowAddExportModal(true)
                                        }
                                    >
                                        <FaPlus
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Add export
                                    </Dropdown.Item>
                                </>
                            )}
                            {mission &&
                                (mission.running ? (
                                    <Dropdown.Item
                                        as={Link}
                                        target="_blank"
                                        to={`${urls.locationMap(organizationId, locationId)}?missionId=${missionId}&droneId=${getMissionDrones(mission)[0]}`}
                                    >
                                        <FaMap
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Preview
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item
                                        onClick={() => setShowResumeModal(true)}
                                    >
                                        <FaPlay
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Resume
                                    </Dropdown.Item>
                                ))}
                            <Dropdown.Item
                                onClick={() => setShowAbortModal(true)}
                            >
                                <FaTimes
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Abort
                            </Dropdown.Item>
                            {mission &&
                                (mission.deleted ? (
                                    <Dropdown.Item
                                        className="text-success"
                                        variant="outline-success"
                                        onClick={() =>
                                            api.restoreMission(
                                                organizationId,
                                                locationId,
                                                missionId,
                                            )
                                        }
                                    >
                                        <FaTrashRestore
                                            className="me-2"
                                            style={{ marginTop: "-2px" }}
                                        />
                                        Restore
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item
                                        className="text-danger"
                                        variant="outline-danger"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        <FaTrash
                                            className="me-2"
                                            style={{
                                                marginTop: "-2px",
                                            }}
                                        />
                                        Delete
                                    </Dropdown.Item>
                                ))}
                        </DropdownButton>
                    </ButtonGroup>
                </Title>
                {mission?.deleted && (
                    <Alert className="mx-4 py-2" variant="danger">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>
                                <FaExclamationTriangle
                                    className="me-2"
                                    style={{ marginTop: "-2px" }}
                                />
                                This mission is deleted!
                            </span>
                            <Button
                                className="py-1"
                                onClick={() =>
                                    api.restoreMission(
                                        organizationId,
                                        locationId,
                                        missionId,
                                    )
                                }
                            >
                                <FaTrashRestore
                                    className="me-2"
                                    style={{ marginTop: "-2px" }}
                                />
                                Restore
                            </Button>
                        </div>
                    </Alert>
                )}
                <MissionHandlerAlerts
                    organizationId={organizationId}
                    locationId={locationId}
                    missionId={missionId}
                />
                <ListGroup variant="flush">
                    {showEvents && <MissionLog events={mission.events} />}
                    {view === "data" && (
                        <ListGroup.Item className="px-4">
                            <CodeBlock name="Mission" code={mission} />
                        </ListGroup.Item>
                    )}
                </ListGroup>
                <Modal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Mission</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete mission{" "}
                        <b>
                            {
                                <TimeDisplay
                                    timestamp={mission.timestamp.seconds}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    organizationId={organizationId}
                                    locationId={locationId}
                                />
                            }
                        </b>
                        ?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() =>
                                deleteMission().then(() =>
                                    navigate(
                                        urls.locationMissions(
                                            organizationId,
                                            locationId,
                                        ),
                                    ),
                                )
                            }
                        >
                            <FaTrash
                                className="me-2"
                                style={{ marginTop: "-2px" }}
                            />
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card>
            <UserQuestion
                organizationId={organizationId}
                locationId={locationId}
                mission={mission}
            ></UserQuestion>
            {view === "default" && (
                <Row className="mt-4">
                    <Col xs={12} md={8}>
                        <MissionAccordion mission={mission} />
                        {getUserIsAdmin() && taskCommands.length > 0 && (
                            <Accordion className="mb-4">
                                <Accordion.Item
                                    eventKey="task-commands"
                                    className="overflow-hidden"
                                >
                                    <Accordion.Button className="py-2">
                                        Task commands ({taskCommands.length})
                                    </Accordion.Button>
                                    <Accordion.Body className="p-0">
                                        <ListGroup variant="flush">
                                            {taskCommands.map(
                                                (command, commandIdx) => (
                                                    <CommandPreview
                                                        // biome-ignore lint/suspicious/noArrayIndexKey: Task commands have no stable identifier and their position is part of their identity.
                                                        key={commandIdx}
                                                        command={command}
                                                        commandIdx={commandIdx}
                                                        className="ps-4"
                                                    />
                                                ),
                                            )}
                                        </ListGroup>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        )}
                    </Col>
                    <Col xs={12} md={4}>
                        <ResultCardList
                            results={resultDocs}
                            className="mb-3 shadow-sm"
                            showTimestamp={false}
                            url={urls.locationResults(
                                organizationId,
                                locationId,
                            )}
                            showGoToMission={false}
                        />

                        <DroneCardList
                            droneIds={mission?.droneIds}
                            organizationId={organizationId}
                            locationId={locationId}
                            showSlider={
                                mission?.states?.collecting ===
                                missionApi.STATE_RUNNING
                            }
                        />
                    </Col>
                </Row>
            )}
        </Layout>
    );
}
